<template>
  <layout
    :settings="settings"
    :is-loading="isSavingWizardPageAnswer"
    :custom-class-name="layoutCustomClassName"
    @restart="onRestartWizard"
    @go-to-landing-page="showLandingPage"
    @prev="onPrev"
    @next="onNext"
    @skip="onSkip"
    @load-more-products="onLoadMoreProducts"
    @progress-bar-item-click="onProgressBarItemClick"
    @search="onSearch"
    @link-from-message-click="onClickLinkFromMessage"
  >
    <spinner v-if="showSpinner" />
    <wizard-page
      v-if="!showSpinner && currentWizardPage && !isCurrentProductsPage && wizardPageData"
      :question="currentWizardPage"
      :question-data="wizardPageData"
      :selections="selectedOptions"
      :show-spinner="showSpinner"
      :option-search-query="optionSearchQuery"
      @select="onSelectQuestion"
    />
    <wizard-page-products
      v-if="!showSpinner && currentWizardPage && isCurrentProductsPage"
      @restart="onRestartWizard"
    />
  </layout>
</template>

<script>
// @flow

import { mapGetters, mapActions, mapState } from 'vuex'

import {
  type QuestionSettings,
  type QuestionSelection,
  isAutoNext
} from '../../../common/wizard'
import { productIds } from '../../../common/models/Product'
import { dispatchChatchampEvent, WIZARD_EVENTS } from '../../../main/eventTracking'

import Spinner from '../Spinner/Spinner.vue'

import Layout from './Layout/Layout.vue'
import WizardPage from './WizardPage/WizardPage.vue'
import WizardPageProducts from './WizardPageProducts/WizardPageProducts.vue'

export default {
  components: {
    Spinner,
    Layout,
    WizardPage,
    WizardPageProducts
  },
  data () {
    return {
      requestTakingTooLong: false,
      optionSearchQuery: null
    }
  },
  computed: {
    ...mapState(['recommendedProducts', 'wizard', 'isLoadingProducts', 'latestLoadedProducts', 'visitedPageIds']),
    ...mapGetters(['avatarImageUrl', 'avatarName', 'currentWizardPage', 'wizardPageIndex', 'selectedOptions', 'isSavingWizardPageAnswer', 'wizardPageData', 'progressBar', 'isCurrentProductsPage', 'isSortingEnabled', 'isSearchEnabled']),
    settings () {
      const settings: QuestionSettings = {
        header: {
          avatar: {
            imageUrl: this.avatarImageUrl,
            name: this.avatarName
          },
          progressBar: {
            progress: this.progressBar.progress,
            items: this.progressBar.visitedPages
          },
          topMessage: this.topMessage,
          showCloseButton: !this.wizard.skipLandingPage,
          isCurrentProductsPage: this.isCurrentProductsPage,
          isSortingEnabled: this.isSortingEnabled,
          isSearchEnabled: this.isSearchEnabled,
          optionSearchQuery: this.optionSearchQuery
        }
      }

      return settings
    },
    topMessage () {
      if (this.isCurrentProductsPage) {
        return {
          title: this.recommendedProducts.textMain,
          description: this.recommendedProducts.textOther
        }
      } else if (this.wizardPageData?.message) {
        return {
          title: this.wizardPageData?.message.title,
          description: this.wizardPageData?.message.description
        }
      }
      return null
    },
    showSpinner () {
      return this.requestTakingTooLong || (this.isLoadingProducts && !this.recommendedProducts?.elements.length)
    },
    layoutCustomClassName (): string {
      return `trbo-a-page-${this.currentWizardPage.depth}-${this.currentWizardPage.id}`
    }
  },
  async created () {
    await this.loadInitialContent()
  },
  destroyed () {
    if (this.requestTimer) clearInterval(this.requestTimer)
  },
  methods: {
    ...mapActions(['showLandingPage', 'restartWizard', 'prevWizardPage', 'loadWizardPages', 'loadMoreProducts', 'selectQuestionOption', 'loadWizardPage', 'saveAndGetNextPage', 'goBackToPage', 'createClickout']),
    onNext () {
      this.resetOptionSearchQuery()
      this.monitorRequest(this.saveAndGetNextPage)
      this.onNavigate()
    },
    onSkip () {
      this.resetOptionSearchQuery()
      this.selectQuestionOption({ options: undefined })
      this.monitorRequest(this.saveAndGetNextPage)
      this.onNavigate()
    },
    onPrev () {
      this.resetOptionSearchQuery()
      this.onNavigate()

      if (this.visitedPageIds.length > 0) return this.monitorRequest(this.prevWizardPage)

      this.showLandingPage()
    },
    onSelectQuestion (payload: QuestionSelection) {
      this.selectQuestionOption(payload)
      if (isAutoNext(this.currentWizardPage?.pageType) || payload?.forceNextPage) {
        this.resetOptionSearchQuery()
        this.monitorRequest(this.saveAndGetNextPage)
      }
      this.onNavigate()
    },
    loadInitialContent () {
      this.monitorRequest(async () => {
        await this.loadWizardPages()
        await this.loadWizardPage(this.wizardPageIndex)
      })
    },
    onProgressBarItemClick (item) {
      this.resetOptionSearchQuery()
      this.monitorRequest(() => this.goBackToPage(item.id))
      this.onNavigate()
    },
    async monitorRequest (request) {
      this.startMonitoringRequest()
      await request()
      this.stopMonitoringRequest()
    },
    startMonitoringRequest () {
      this.requestTimer = setTimeout(() => {
        this.requestTakingTooLong = true
      }, 500)
    },
    stopMonitoringRequest () {
      clearTimeout(this.requestTimer)
      this.requestTakingTooLong = false
    },
    onRestartWizard () {
      this.restartWizard()
      this.onNavigate()
      dispatchChatchampEvent(WIZARD_EVENTS.RestartedEvent)
    },
    onNavigate () {
      this.$emit('navigate')
    },
    async onLoadMoreProducts () {
      await this.loadMoreProducts()
      dispatchChatchampEvent(WIZARD_EVENTS.MoreProductsLoadedEvent, {
        customerId: this.$store.state.customerId,
        subscriberId: this.$store.state.subscriberId,
        wizardId: this.$store.state.wizard.id,
        productIds: productIds(this.latestLoadedProducts),
        products: this.latestLoadedProducts
      })
    },
    onSearch (query: string): void {
      this.optionSearchQuery = query
    },
    resetOptionSearchQuery (): void {
      this.optionSearchQuery = null
    },
    onClickLinkFromMessage (url: string): void {
      this.createClickout(url)
    }
  }
}
</script>
